import { DOCUMENTS_URL } from '../../../../../lib/_api-helpers';
import { ContentColumn, LearnerActivityBase } from '../../../components/shared';
import strings from '../../../../../strings/strings.json';
import {
    BodyLarge,
    FlexColMd,
    FlexColXs,
    FlexLg,
    FlexSmAlignCenter,
    Headline2,
    HorizontalRule,
} from '../../../../../components/Elements';
import TimeIcon from '../../../../../assets/icons/pathway/TimeIcon';
import { breakpoints, sizes, theme } from '../../../../../theme';
import ScheduleIcon from '../../../../../assets/icons/pathway/ScheduleIcon';
import LocationIcon from '../../../../../assets/icons/controls/LocationIcon';
import { IconSize } from '../../../../../assets/icons/icon-sizes';
import styled from 'styled-components';
import { LocationType } from '../../../../../models/ActivityMeeting';
import { CourseNavigationActivity } from '../../../components/navigation/CourseNavigationActivity';
import { LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import { LearnerActivity } from 'models/LearnerCourse';
import PresentationSlidesSection, { Slide } from 'pages/schedule/courses/live-training/PresentationSlidesSection';
import ActivityHeader from '../ActivityHeader';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import SuccessNotifier from 'components/notifiers/SuccessNotifier';
import Button from 'components/buttons/Button';

type LearnerLiveTrainingProps = {
    liveTraining: LearnerActivity;
    slides: Slide[];
};

export default function LearnerLiveTraining({ liveTraining, slides }: LearnerLiveTrainingProps): JSX.Element {
    const meetingDate = liveTraining.startDate ? new Date(liveTraining.startDate) : new Date();

    return (
        <LearnerActivityBase>
            <ContentColumn>
                <ActivityHeader activity={liveTraining} />

                <DateTimeLocation
                    meetingDate={meetingDate}
                    durationInMins={liveTraining.durationInMins}
                    locationType={liveTraining.locationType}
                    location={liveTraining.location}
                    buttons={[
                        {
                            title: strings.liveTrainingPage.joinMeetingButton,
                            aria: strings.liveTrainingPage.joinMeetingButtonAria,
                            onClick: () => window.open(liveTraining.joinLink),
                            icon: <></>,
                            autoFocus: true,
                        },
                    ]}
                />

                {!liveTraining.completed ? (
                    <Notification
                        notificationType={NotificationType.INFO}
                        title={strings.liveTrainingPage.attendanceInfo}
                    />
                ) : (
                    <SuccessNotifier title={strings.liveTrainingPage.attendanceSuccess} description={''} />
                )}

                <HorizontalRule />

                {liveTraining.videoDocument && (
                    <>
                        <FlexColMd>
                            <Headline2>{strings.liveTrainingPage.videoRecordingHeader}</Headline2>
                            <video
                                controls
                                controlsList="nodownload"
                                src={`${DOCUMENTS_URL}/${liveTraining.videoDocument.s3Filepath}`}
                                width="100%"
                            />
                        </FlexColMd>
                        <HorizontalRule />
                    </>
                )}

                <PresentationSlidesSection slides={slides} />
            </ContentColumn>
            <Footer>
                <CourseNavigationActivity />
            </Footer>
        </LearnerActivityBase>
    );
}

export type ButtonDetails = {
    title: string;
    aria: string;
    onClick: () => void;
    icon: JSX.Element;
    autoFocus?: boolean;
};

type DateTimeLocationProps = {
    meetingDate: Date;
    durationInMins: number | null;
    locationType: string | undefined;
    location: string | undefined;
    buttons: ButtonDetails[];
};

export function DateTimeLocation({
    meetingDate,
    durationInMins,
    locationType,
    location,
    buttons,
}: DateTimeLocationProps): JSX.Element {
    const dateObj = new Date(meetingDate);

    const formattedDate = dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });

    const meetingStartTimeString = dateObj
        .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h12' })
        .replaceAll(' ', '');

    const meetingEndTime = new Date(dateObj.getTime() + 60000 * (durationInMins ?? 1));

    const meetingEndTimeString = meetingEndTime
        .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h12' })
        .replaceAll(' ', '');

    const locationFriendlyText = locationType == LocationType.ONLINE ? 'Online' : `In person - ${location}`; //eslint-disable-line eqeqeq

    return (
        <DateTimeLocationCard>
            <FlexLg>
                <Field>
                    <FlexSmAlignCenter>
                        <ScheduleIcon colour={theme.fiord} size={IconSize.SMALL} />
                        <BodyLarge $bold $nowrap>
                            {strings.liveTrainingPage.dateLabel}
                        </BodyLarge>
                    </FlexSmAlignCenter>
                    <BodyLarge $nowrap>{formattedDate}</BodyLarge>
                </Field>

                <Field>
                    <FlexSmAlignCenter>
                        <TimeIcon colour={theme.fiord} size={IconSize.SMALL} />
                        <BodyLarge $bold $nowrap>
                            {strings.liveTrainingPage.timeLabel}
                        </BodyLarge>
                    </FlexSmAlignCenter>
                    <BodyLarge $nowrap>
                        {meetingStartTimeString} - {meetingEndTimeString}
                    </BodyLarge>
                </Field>
            </FlexLg>

            <Field>
                <FlexSmAlignCenter>
                    <LocationIcon colour={theme.fiord} size={IconSize.SMALL} />
                    <BodyLarge $bold $nowrap>
                        {strings.liveTrainingPage.locationLabel}
                    </BodyLarge>
                </FlexSmAlignCenter>
                <BodyLarge $nowrap>{locationFriendlyText}</BodyLarge>
            </Field>

            <ButtonsContainer>
                {buttons.map((b, i) => (
                    <div key={i}>
                        {
                            <Button
                                title={b.title}
                                aria={b.aria}
                                onClick={b.onClick}
                                icon={b.icon}
                                autoFocus={b.autoFocus}
                            />
                        }
                    </div>
                ))}
            </ButtonsContainer>
        </DateTimeLocationCard>
    );
}

const ButtonsContainer = styled(FlexColMd)`
    height: 100%;
    align-items: flex-end;

    @media (max-width: ${breakpoints.md}) {
        flex-direction: row;
    }

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        width: 100%;
        align-items: stretch;
    }
`;

const DateTimeLocationCard = styled(LearnerCardBase)`
    display: grid;
    padding: ${sizes.spacingMd};
    gap: ${sizes.spacingMd};
    align-items: center;

    grid-template-rows: auto auto;
    grid-template-columns: auto 1fr auto; /* date, time, spacer, button */

    /* Date Time */
    & > :nth-child(1) {
        grid-row: 1;
        grid-column: 1;
    }

    /* Location */
    & > :nth-child(2) {
        grid-row: 2;
        grid-column: 1;
    }

    /* Buttons */
    & > :nth-child(3) {
        grid-row: 1 / span 2; /* occupy both rows */
        grid-column: 3; /* right column */
    }

    @media (max-width: ${breakpoints.md}) {
        grid-template-rows: auto auto auto;
        grid-template-columns: auto;

        /* Button - move to bottom */
        & > :nth-child(3) {
            grid-row: 3;
            grid-column: 1;
        }
    }
`;

const Field = styled(FlexColXs)`
    width: unset;
`;

const Footer = styled(FlexColMd)`
    width: 100%;
    align-items: center;
`;
